export type SearchFilterValueSelectionMode = 'multiple' | 'single' | 'tags';

export interface SearchFilterFieldMetadata {
    label: string;
    options?: Array<{ label: string; value: string }>;
    selectionMode: SearchFilterValueSelectionMode;
}

const options = (values: ReadonlyArray<readonly [label: string, value: string]>) =>
    values.map(([label, value]) => ({ label, value }));

const searchFilterFieldMetadata: Record<string, SearchFilterFieldMetadata> = {
    'companies.assetClasses': {
        label: 'Asset Type',
        options: options([
            ['Public · Companies', 'public:company'],
            ['Public · Funds', 'public:fund'],
            ['Public · Indices', 'public:index'],
            ['Private · Company', 'private:company'],
            ['Private · Fund', 'private:fund'],
            ['Private · Entity', 'private:entity'],
            ['Private · Other', 'private:other'],
        ]),
        selectionMode: 'multiple',
    },
    'companies.exchanges': { label: 'Exchanges', selectionMode: 'tags' },
    'companies.industries': { label: 'Industries', selectionMode: 'tags' },
    'companies.location': { label: 'Locations (HQ)', selectionMode: 'tags' },
    'companies.sectors': { label: 'Sectors', selectionMode: 'tags' },
    'companies.attributes': { label: 'Attributes', selectionMode: 'tags' },
    'companies.employeeHeadcount': { label: 'Employee Headcount', selectionMode: 'multiple' },
    'companies.fiscalYearEnd': { label: 'Fiscal Year-End', selectionMode: 'multiple' },
    'companies.fundraising': { label: 'Fundraising', selectionMode: 'multiple' },
    'companies.webTraffic': { label: 'Website Traffic', selectionMode: 'multiple' },
    'documents.languages': {
        label: 'Languages',
        options: options([
            ['English', 'en'],
            ['Spanish', 'es'],
            ['French', 'fr'],
            ['German', 'de'],
            ['Chinese', 'zh'],
            ['Japanese', 'ja'],
            ['Portuguese', 'pt'],
            ['Italian', 'it'],
            ['Korean', 'ko'],
            ['Arabic', 'ar'],
        ]),
        selectionMode: 'tags',
    },
    'documents.formats': {
        label: 'File Formats',
        options: options([
            ['PDF', 'pdf'],
            ['DOCX', 'docx'],
            ['EPUB', 'epub'],
            ['Spreadsheet', 'sheet'],
            ['Presentation', 'slide'],
            ['Text', 'text'],
        ]),
        selectionMode: 'multiple',
    },
    'documents.types': {
        label: 'Document Types',
        options: options([
            ['Connected Documents', 'connected'],
            ['Public Company Filings', 'publicFilings'],
        ]),
        selectionMode: 'multiple',
    },
    'documents.sources': {
        label: 'Sources',
        options: options([
            ['SEC', 'sec'],
            ['Platform Financials', 'platformFinancials'],
            ['Box', 'box'],
            ['Coda', 'coda'],
            ['Confluence', 'confluence'],
            ['DocuSign', 'docusign'],
            ['Dropbox', 'dropbox'],
            ['Google Drive', 'googleDrive'],
            ['Google Sheets', 'googleSheets'],
            ['Miro', 'miro'],
            ['Notion', 'notion'],
            ['Slack', 'slack'],
            ['Smartsheet', 'smartsheet'],
        ]),
        selectionMode: 'multiple',
    },
    'documents.sentiments': {
        label: 'Sentiment',
        options: options([
            ['Positive', 'Positive'],
            ['Neutral', 'Neutral'],
            ['Negative', 'Negative'],
        ]),
        selectionMode: 'multiple',
    },
    'economic.categories': {
        label: 'Dataset Categories',
        options: options([
            ['Macroeconomic Indicators', 'macro'],
            ['US Treasury Rates', 'treasury'],
            ['Markets, Commodities, Forex & Crypto', 'market'],
        ]),
        selectionMode: 'multiple',
    },
    'economic.sources': {
        label: 'Sources',
        options: options([
            ['Platform Financials', 'fmp'],
            ['FRED', 'fred'],
        ]),
        selectionMode: 'multiple',
    },
    'economic.frequencies': {
        label: 'Frequency',
        options: options([
            ['Daily', 'daily'],
            ['Monthly', 'monthly'],
            ['Quarterly', 'quarterly'],
            ['Annual', 'annual'],
        ]),
        selectionMode: 'multiple',
    },
    'events.coverage': {
        label: 'Coverage',
        selectionMode: 'single',
        options: options([
            ['Key Research Events', 'research'],
            ['All Events', 'all'],
        ]),
    },
    'events.countries': {
        label: 'Countries',
        selectionMode: 'tags',
        options: options([
            ['United States', 'US'],
            ['United Kingdom', 'GB'],
            ['Germany', 'DE'],
            ['France', 'FR'],
            ['Japan', 'JP'],
            ['China', 'CN'],
            ['Canada', 'CA'],
            ['Australia', 'AU'],
        ]),
    },
    'events.currencies': {
        label: 'Currencies',
        selectionMode: 'tags',
        options: ['USD', 'EUR', 'GBP', 'JPY', 'CNY', 'CAD', 'AUD', 'CHF'].map((value) => ({ label: value, value })),
    },
    'events.symbols': { label: 'Dividend Symbols', selectionMode: 'tags' },
    'events.impacts': {
        label: 'Economic Impact',
        options: options([
            ['High Impact', 'high'],
            ['Medium Impact', 'medium'],
            ['Low Impact', 'low'],
        ]),
        selectionMode: 'multiple',
    },
    'events.sources': {
        label: 'Calendar Sources',
        options: options([
            ['No Calendar Sources', 'none'],
            ['Dividend Calendar', 'dividend'],
            ['Economic Calendar', 'economic'],
        ]),
        selectionMode: 'multiple',
    },
    'news.feeds': {
        label: 'News Feeds',
        options: options([
            ['Mainstream', 'Mainstream'],
            ['Press Releases', 'Press Releases'],
            ['Currencies', 'Currencies'],
            ['Cryptocurrencies', 'Cryptocurrencies'],
        ]),
        selectionMode: 'multiple',
    },
    'news.types': {
        label: 'Coverage Types',
        options: options([
            ['News', 'market'],
            ['Insider Transactions', 'insiderTransactions'],
            ['IPOs', 'ipos'],
            ['M&A', 'mAndA'],
            ['Fundraising & Crowdfunding', 'fundraising'],
        ]),
        selectionMode: 'multiple',
    },
    'people.jobTitles': {
        label: 'Job Titles',
        selectionMode: 'tags',
        options: [
            'Chief Executive Officer',
            'Chief Financial Officer',
            'Chief Investment Officer',
            'Portfolio Manager',
            'Investment Analyst',
            'Managing Director',
            'Partner',
            'Founder',
        ].map((value) => ({ label: value, value })),
    },
    'people.educationLevels': {
        label: 'Education Levels',
        options: options([
            ['High School', 'high_school'],
            ['Associate', 'associate'],
            ["Bachelor's", 'bachelors'],
            ["Master's", 'masters'],
            ['Doctorate', 'doctorate'],
        ]),
        selectionMode: 'multiple',
    },
    'people.roles': {
        label: 'Functional Roles',
        options: options([
            ['Accounting', 'accounting'],
            ['Education', 'education'],
            ['Engineering', 'engineering'],
            ['Finance', 'finance'],
            ['Health', 'health'],
            ['Human Resources', 'human_resources'],
            ['Legal', 'legal'],
            ['Marketing', 'marketing'],
            ['Operations', 'operations'],
            ['Real Estate', 'real_estate'],
            ['Sales', 'sales'],
        ]),
        selectionMode: 'multiple',
    },
    'people.sources': {
        label: 'Connected & Enriched Sources',
        options: options([
            ['Gmail', 'Gmail'],
            ['Microsoft 365 Outlook', 'Microsoft 365 Outlook'],
            ['People Data Labs Enrichment', 'enrichment'],
            ['Platform Financials Executives', 'fmpExecutives'],
        ]),
        selectionMode: 'multiple',
    },
    'trade.modes': {
        label: 'Transport Modes',
        options: options([
            ['Ocean', 'ocean'],
            ['Air', 'air'],
            ['Rail', 'rail'],
            ['Road', 'road'],
        ]),
        selectionMode: 'multiple',
    },
    'trade.regions': {
        label: 'Regions',
        options: options(
            ['Africa', 'Asia Pacific', 'Europe', 'Latin America', 'Middle East', 'North America'].map(
                (region) => [region, region] as const,
            ),
        ),
        selectionMode: 'multiple',
    },
    'trade.sourceIds': { label: 'Data Sources', selectionMode: 'multiple' },
    'workspace.resourceTypes': {
        label: 'Resource Types',
        options: options([
            ['Folders', 'folders'],
            ['Investments', 'investments'],
            ['Memos', 'memos'],
            ['Pipelines', 'pipelines'],
            ['Teams', 'teams'],
            ['Dashboards', 'dashboards'],
            ['Lists', 'lists'],
        ]),
        selectionMode: 'multiple',
    },
    'workspace.tags': { label: 'Tags', selectionMode: 'multiple' },
    'workspace.updatedWithin': {
        label: 'Last Updated',
        options: options([
            ['Past 24 hours', 'day'],
            ['Past 7 days', 'week'],
            ['Past 30 days', 'month'],
            ['Past 90 days', 'quarter'],
        ]),
        selectionMode: 'single',
    },
};

const canonicalFieldPath = (path: string) => {
    const segments = path.split('.');

    if (segments[0] === 'searchDomains' && segments.length >= 3) {
        return `${segments[1]}.${segments[2]}`;
    }

    return `companies.${segments.at(-1) ?? path}`;
};

export const getSearchFilterFieldMetadata = (path: string): SearchFilterFieldMetadata | undefined =>
    searchFilterFieldMetadata[canonicalFieldPath(path)];
